/**
 * Master URL list for primary nav, main nav, secondary nav.
 *
 * We do NOT need to include all site links here.
 *
 * @todo: make this include a pair of set/maps which can be iterated and more
 * dynamic. Possibly even populated from contentful, or a JSON file from contentful.
 *
 * @module data/URLS
 */

/**
 * Format for gatsby links
 * @param {string} p - string to make a url
 * @returns {string} - formatted for links
 * @todo RESOLVE CIRCULAR IMPORTS FROM UTILS
 * @see copy of this in utils/index
 */
function formatPathname(p) {
  return p.slice(-1) !== "/" ? `${p}/` : p
}

/**
 * @private Used to key up the PAGE_KEYS, PAGE_NAMES, and URLS
 * ******* IMPORTANT: ADD NEW NAMES TO ALL THREE SETS *******
 * constants
 */
const pageStrings = [
  "HOMEPAGE",
  "EXO_WORKS",
  "BOOK_DEMO",
  "BOOK_DEMO_THANK_YOU",
  "TECH_SPECS",
  "PRESS_KIT",
  "EXO_IRIS",
  "VISION",
  "TECHNOLOGY",
  "RESOURCES",
  "NEWSROOM",
  "OUR_PEOPLE",
  "LEADERSHIP",
  "THOUGHT_LEADERSHIP",
  "CAREERS",
  "SUPPORT",
  "CONTACT",
  "INVESTORS",
  "LEGAL",
  "POLICY_PRIVACY",
  "POLICY_COOKIE",
  "POLICY_SOCIAL",
  "FOUROHFOUR",
  // @todo: remove
  "STYLEGUIDE",
  "SITEMAP"
]

// create PAGE_KEYS constants to re-use
const PAGE_KEYS = {}
pageStrings.forEach(i => {
  PAGE_KEYS[i] = i
})

/**
 * This set of "names" is used to just "tag" certain things related
 * to the page, for example `page__${PAGE_NAMES.HOME}` might
 * be passed as a class from the SASS to the page module.
 * @note  exported from constants as a convenience
 * ******* IMPORTANT: ADD NEW NAMES TO ALL THREE SETS *******
 * THE NAMES USE UNDERSCORES IN CASE USED WITH CSS MODULES
 */
export const PAGE_NAMES = {
  [PAGE_KEYS.HOMEPAGE]: "home",
  [PAGE_KEYS.EXO_WORKS]: "exo_works",
  [PAGE_KEYS.BOOK_DEMO]: "book_demo",
  [PAGE_KEYS.BOOK_DEMO_THANK_YOU]: "book_demo_thank_you",
  [PAGE_KEYS.TECH_SPECS]: "tech_specs", // page type, not page
  [PAGE_KEYS.PRESS_KIT]: "press-kit",
  [PAGE_KEYS.EXO_IRIS]: "exo_iris",
  [PAGE_KEYS.VISION]: "vision",
  [PAGE_KEYS.TECHNOLOGY]: "technology",
  [PAGE_KEYS.RESOURCES]: "resources",
  [PAGE_KEYS.NEWSROOM]: "newsroom",
  [PAGE_KEYS.OUR_PEOPLE]: "our_people",
  [PAGE_KEYS.LEADERSHIP]: "leadership",
  [PAGE_KEYS.THOUGHT_LEADERSHIP]: "thought_leadership",
  [PAGE_KEYS.CAREERS]: "careers",
  [PAGE_KEYS.SUPPORT]: "support",
  [PAGE_KEYS.CONTACT]: "contact",

  [PAGE_KEYS.INVESTORS]: "investors",

  [PAGE_KEYS.LEGAL]: "legal",
  [PAGE_KEYS.POLICY_PRIVACY]: "privacy",
  [PAGE_KEYS.POLICY_COOKIE]: "cookie",
  [PAGE_KEYS.POLICY_SOCIAL]: "social",

  [PAGE_KEYS.INVESTORS]: "investors",
  [PAGE_KEYS.FOUROHFOUR]: "404",
  // @todo: remove
  [PAGE_KEYS.STYLEGUIDE]: "styleguide",
  [PAGE_KEYS.SITEMAP]: "sitemap"
}

/**
 * This set of url paths should match the slugs for the top level pages
 * coming from Contentful.
 * ******* IMPORTANT: ADD NEW NAMES TO ALL THREE SETS *******
 */
export default {
  [PAGE_KEYS.HOMEPAGE]: formatPathname("/"),
  [PAGE_KEYS.EXO_WORKS]: formatPathname("/exo-works"),
  [PAGE_KEYS.BOOK_DEMO]: formatPathname("/book-demo"),
  [PAGE_KEYS.BOOK_DEMO_THANK_YOU]: formatPathname("/book-demo/thank-you"),
  [PAGE_KEYS.TECH_SPECS]: prodSlug => formatPathname(`/${prodSlug}/tech-specs`),
  [PAGE_KEYS.EXO_IRIS]: formatPathname("/exo-iris"),
  [PAGE_KEYS.PRESS_KIT]: formatPathname("/press-kit"),
  [PAGE_KEYS.VISION]: formatPathname("/our-vision"),
  [PAGE_KEYS.TECHNOLOGY]: formatPathname("/technology"),
  [PAGE_KEYS.RESOURCES]: formatPathname("/resources"),
  [PAGE_KEYS.NEWSROOM]: formatPathname("/newsroom"),
  [PAGE_KEYS.OUR_PEOPLE]: formatPathname("/our-people"),
  [PAGE_KEYS.LEADERSHIP]: formatPathname("/leadership"),
  [PAGE_KEYS.THOUGHT_LEADERSHIP]: formatPathname("/thought-leadership"),
  [PAGE_KEYS.CAREERS]: formatPathname("/careers"),

  // note: near term there is only exo-works support; longer term, remove second and pass in a slug
  [PAGE_KEYS.SUPPORT]: slug =>
    slug ? formatPathname(`/support/${slug}`) : "https://support.exo.inc/hc/en-us",
  [PAGE_KEYS.CONTACT]: formatPathname("/contact"),

  [PAGE_KEYS.INVESTORS]: formatPathname("/investors"),

  [PAGE_KEYS.LEGAL]: formatPathname("/legal"), // not sure there even is a direct page ...

  [PAGE_KEYS.POLICY_PRIVACY]: formatPathname("/legal/privacy-policy"),
  [PAGE_KEYS.POLICY_COOKIE]: formatPathname("/legal/cookie-policy"),
  [PAGE_KEYS.POLICY_SOCIAL]: formatPathname("/legal/social-policy"),
  [PAGE_KEYS.FOUROHFOUR]: formatPathname("/404"),
  // @todo: remove
  [PAGE_KEYS.STYLEGUIDE]: formatPathname("/styleguide"),

  [PAGE_KEYS.SITEMAP]: formatPathname("/sitemap")
}
